import { intToString } from "app/uicomponents/macrocomponents/PolicySummary/config";
import { getObjectFromSessionStore, getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import {
  getThemedPrimarySecondaryFromSource,
  getTooltipColors,
  getUSANumberFormat,
} from "app/utils/utils";
import ReactECharts from "echarts-for-react";
import React, { useCallback, useRef, useState } from "react";

interface ChartDataPoint {
  year: number;
  planYear?: number;
  insuredAge?: number;
  accountValue?: number | null;
  assetValue?: number | null;
  surrenderValue?: number | null;
  outsideAssets?: number | null;
  outsideAccountValue?: number | null;
  benefit?: number | null;
  benefitValue?: number | null;
  financing?: number | null;
  financingValue?: number | null;
  // Projection data
  projectedAssetValue?: number | null;
  projectedAccountValue?: number | null;
  accountValueProjected?: number | null;
  projectedSurrenderValue?: number | null;
  surrenderValueProjected?: number | null;
  projectedOutsideAssets?: number | null;
  outsideAssetsProjected?: number | null;
  projectedOutsideAccountValue?: number | null;
  projectedBenefit?: number | null;
  projectedBenefitValue?: number | null;
  benefitProjected?: number | null;
  projectedFinancing?: number | null;
  projectedFinancingValue?: number | null;
  financingProjected?: number | null;
}

interface AssetValueLineChartProps {
  data?: ChartDataPoint[];
  loading?: boolean;
  height?: string;
  className?: string;
  selectedChartFilter?: string;
  // primary or list of selected filters. When array is provided, render all.
  selectedFilter?: string | string[];
  selectedFilters?: string[];
  showProjections?: boolean;
  isPolicy?: boolean;
  // When false, ignore branding colors for series and use defaults
  useBrandingColors?: boolean;
  appendSeries?: object[];
  firstLineSeriesExtras?: Record<string, unknown>;
  chartGridTop?: string;
  tooltipFormatterOverride?: ((params: any) => string) | null;
  showActualValues?: boolean;
}

const applyHexOpacity = (hexColor: string, opacity: number) => {
  const normalized = Math.max(0, Math.min(1, opacity));

  const rgbMatch = hexColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return `rgba(${r}, ${g}, ${b}, ${normalized})`;
  }

  const alpha = Math.round(normalized * 255)
    .toString(16)
    .padStart(2, "0");

  if (!hexColor.startsWith("#")) {
    return hexColor;
  }

  if (hexColor.length === 4) {
    const r = hexColor[1];
    const g = hexColor[2];
    const b = hexColor[3];
    return `#${r}${r}${g}${g}${b}${b}${alpha}`;
  }

  if (hexColor.length === 7) {
    return `${hexColor}${alpha}`;
  }

  return hexColor;
};

const INITIAL_VISIBLE_RECORDS = 10;

const AssetValueLineChart: React.FC<AssetValueLineChartProps> = ({
  data = [],
  loading = false,
  height = "365px",
  className = "",
  selectedChartFilter = "year",
  selectedFilter = "assetValue",
  selectedFilters,
  showProjections = false,
  isPolicy = false,
  useBrandingColors = true,
  appendSeries,
  firstLineSeriesExtras,
  chartGridTop,
  tooltipFormatterOverride,
  showActualValues = false,
}) => {
  const zoomStateRef = useRef<{ start: number; end: number } | null>(null);
  const selectedSeriesRef = useRef<string | null>(null);
  const echartsRef = useRef<any>(null);
  const [alignTooltip, setAlignTooltip] = useState<{
    x: number;
    y: number;
    dataIndex: number;
    acIQ: number | null;
    alignIQ: number | null;
    interestCredits: number | null;
    planYear: number;
    year: number;
    insuredAge: number;
  } | null>(null);
  const globalTheme = getSessionStore("globalTheme");
  const { branding } = getSessionStore("lookUpResponse");
  const { brandingData } = getObjectFromSessionStore("activeBrandingUser");
  const lookupThemed = getThemedPrimarySecondaryFromSource(
    branding,
    "rgb(0, 71, 129)",
    "rgb(0, 135, 232)",
  );
  const dataThemed = brandingData?.[0]
    ? getThemedPrimarySecondaryFromSource(
        brandingData[0],
        lookupThemed.primaryColor,
        lookupThemed.secondaryColor,
      )
    : lookupThemed;
  const effectivePrimaryColor = dataThemed.primaryColor;
  const effectiveSecondaryColor = dataThemed.secondaryColor;

  const tooltipColors = getTooltipColors({
    ...branding,
    primaryColor: effectivePrimaryColor,
    secondaryColor: effectiveSecondaryColor,
  });
  const chartData: ChartDataPoint[] = data as ChartDataPoint[];

  // Color scheme for different data series
  const getSeriesColor = (filterId: string) => {
    if (useBrandingColors) {
      const primary = effectivePrimaryColor || "#004781";
      const colorMap: Record<string, string> = {
        accountValue: applyHexOpacity(primary, 0.6),
        surrenderValue: applyHexOpacity(primary, 0.4),
        deathBenefit: applyHexOpacity(primary, 0.302),
        benefit: applyHexOpacity(primary, 0.302),
        income: applyHexOpacity(primary, 0.2),
        financing: "#757575",
        outsideAssets: applyHexOpacity(primary, 0.4),
      };
      return colorMap[filterId] ?? primary;
    }
    const colors = {
      accountValue: "#0053D0",
      surrenderValue: isPolicy ? "#5599FF" : "#0053D0",
      outsideAssets: "#0053D0",
      benefit: effectivePrimaryColor || "#5599FF",
      financing: effectiveSecondaryColor || "#92BDFF",
    };
    return colors[filterId as keyof typeof colors] || "#004781";
  };

  const getNumericValue = useCallback((item: ChartDataPoint, keys: string[]) => {
    const source = item as unknown as Record<string, unknown>;
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const value = source?.[key];
      if (value !== null && value !== undefined) {
        if (typeof value === "number") {
          return Number.isNaN(value) ? null : value;
        }
        if (typeof value === "string") {
          const parsed = Number(value.toString().replace(/,/g, ""));
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
      }
    }
    return null;
  }, []);

  const calculateChartData = useCallback(() => {
    const datasets: any[] = [];

    const filtersToRender: string[] =
      selectedFilters && selectedFilters.length > 0
        ? selectedFilters
        : selectedFilter
        ? Array.isArray(selectedFilter)
          ? selectedFilter
          : [selectedFilter]
        : [];

    const baseValueKeys: Record<string, string[]> = {
      accountValue: ["accountValue", "assetValue"],
      surrenderValue: ["surrenderValue"],
      outsideAssets: ["outsideAssets", "outsideAccountValue", "yearToDateValue"],
      benefit: ["benefit", "benefitValue"],
      deathBenefit: ["benefit", "benefitValue"],
      financing: ["financing", "financingValue"],
      income: ["income", "totalIncome", "annualIncome"],
    };

    const projectionValueKeys: Record<string, string[]> = {
      accountValue: ["projectedAccountValue", "projectedAssetValue", "accountValueProjected"],
      surrenderValue: ["projectedSurrenderValue", "surrenderValueProjected"],
      outsideAssets: [
        "projectedOutsideAssets",
        "outsideAssetsProjected",
        "projectedOutsideAccountValue",
        "projectedYearToDateValue",
      ],
      benefit: ["projectedBenefit", "projectedBenefitValue", "benefitProjected"],
      deathBenefit: ["projectedBenefit", "projectedBenefitValue", "benefitProjected"],
      financing: ["projectedFinancing", "projectedFinancingValue", "financingProjected"],
      income: ["projectedIncome", "projectedTotalIncome"],
    };

    filtersToRender.forEach((filterKey) => {
      const color = getSeriesColor(filterKey);
      const seriesName =
        filterKey === "accountValue"
          ? "Account Value"
          : filterKey === "surrenderValue"
          ? "Surrender Value"
          : filterKey === "outsideAssets"
          ? "Outside Assets"
          : filterKey === "benefit"
          ? "Benefit"
          : filterKey === "deathBenefit"
          ? "Death Benefit"
          : "Financing";

      if (showProjections) {
        const projectedColor = applyHexOpacity(color, 0.5);

        datasets.push({
          name: `${seriesName} (Projected)`,
          type: "line",
          z: 1,
          data: chartData?.map((item: ChartDataPoint) => {
            const keys = projectionValueKeys[filterKey] || [];
            return keys.length ? getNumericValue(item, keys) : null;
          }),
          smooth: true,
          showSymbol: false,
          connectNulls: false,
          lineStyle: { color: projectedColor, width: 2, type: "dashed" },
          itemStyle: { color: projectedColor },
          areaStyle: { show: false },
        });
      }

      datasets.push({
        name: seriesName,
        type: "line",
        z: 2,
        data: chartData?.map((item: ChartDataPoint) => {
          const keys = baseValueKeys[filterKey] || [];
          return keys.length ? getNumericValue(item, keys) : null;
        }),
        smooth: true,
        showSymbol: showActualValues,
        connectNulls: false,
        lineStyle: {
          color,
          width: 3,
        },
        itemStyle: { color },
        label: showActualValues
          ? {
              show: true,
              position: "top",
              fontSize: 10,
              fontWeight: 600,
              color,
              formatter: (params: any) =>
                params.value != null ? `$${intToString(params.value, true)}` : "",
            }
          : { show: false },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: applyHexOpacity(color, 0.25) },
              { offset: 1, color: applyHexOpacity(color, 0) },
            ],
          },
        },
      });
    });

    return datasets;
  }, [chartData, selectedFilter, selectedFilters, showProjections, getNumericValue]);

  const getChartLabelsData = () => {
    if (selectedChartFilter === "insuredAge") {
      return chartData?.map((item: ChartDataPoint) => `A${item?.insuredAge}`);
    }
    if (selectedChartFilter === "planYear") {
      return chartData?.map((item: ChartDataPoint) => item?.planYear);
    }
    return chartData?.map((item: ChartDataPoint) => item?.year);
  };

  const color = globalTheme === THEME.DARK ? "#ffffff" : "#333333";

  const createTooltipHTML = (params: any) => {
    // Get current data point for dynamic values
    const currentData = chartData?.[params[0]?.dataIndex];
    const year = currentData?.year || 2025;
    const age = currentData?.insuredAge;
    const planYear = currentData?.planYear || 1;

    const header = `<div style="
      background: ${globalTheme === THEME.DARK ? "#3A3A50" : "#E5E7EB"};
      color: ${globalTheme === THEME.DARK ? color : "#374151"};
      padding: 8px 12px;
      text-align: center;
      font-weight: bold;
      font-family: 'Conthrax Semi-Bold', 'Arial', sans-serif;
      border-bottom: 1px solid ${globalTheme === THEME.DARK ? "#4A4A60" : "#e0e0e0"};
      border-radius: 8px 8px 0 0;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 12px;
    ">
      <div style="display: flex; align-items: center; gap: 6px;">
        <i class="pi pi-calendar" style="font-size: 16px; color: ${
          globalTheme === THEME.DARK ? "#ffffff" : "#1F2937"
        }; font-weight: bold;"></i>
        <span style="font-weight: bold;">${year}</span>
      </div>
      <div style="width: 1px; height: 20px; background: ${
        globalTheme === THEME.DARK ? "#ffffff" : "#9CA3AF"
      }; opacity: 0.4;"></div>
      <div style="display: flex; align-items: center; gap: 6px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style="color: ${
          globalTheme === THEME.DARK ? "#ffffff" : "#1F2937"
        }; transform: translateY(1px);">
          <rect x="4" y="12" width="16" height="3" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <rect x="6" y="8" width="12" height="4" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <rect x="8" y="4" width="8" height="4" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <line x1="12" y1="2" x2="12" y2="4" stroke="currentColor" stroke-width="1.5"/>
          <path d="M11 2L12 1L13 2" stroke="currentColor" stroke-width="1.5" fill="none"/>
        </svg>
        <span style="font-weight: bold;">${planYear}</span>
      </div>
      ${
        age
          ? `<div style="width: 1px; height: 20px; background: ${
              globalTheme === THEME.DARK ? "#ffffff" : "#9CA3AF"
            }; opacity: 0.4;"></div>
        <div style="display: flex; align-items: center; gap: 6px;">
        <i class="pi pi-user" style="font-size: 16px; color: ${
          globalTheme === THEME.DARK ? "#ffffff" : "#1F2937"
        }; font-weight: bold;"></i>
        <span style="font-weight: bold;">A${age}</span>
      </div>`
          : ""
      }
    </div>`;

    let content = '<div style="padding: 8px 12px;">';

    params.forEach((param: any) => {
      if (param.value !== null && param.value !== undefined) {
        const bulletColor = param.color || "#4A90E2";
        const isHighlighted = param.seriesName === selectedSeriesRef.current;
        const isProjection = param.seriesName.includes("(Projected)");

        content += `<div style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 8px;
          margin: 2px 0;
          border-bottom: 1px solid ${globalTheme === THEME.DARK ? "#4A4A60" : "#E5E7EB"};
          ${isHighlighted ? `background: ${tooltipColors.primaryColor}20; border-radius: 4px;` : ""}
        ">
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background-color: ${isHighlighted ? tooltipColors.secondaryColor : bulletColor};
              flex-shrink: 0;
              ${
                isProjection
                  ? `border: 2px solid ${bulletColor}; background-color: transparent;`
                  : ""
              }
            "></div>
            <span style="
              color: ${
                isHighlighted
                  ? tooltipColors.secondaryColor
                  : globalTheme === THEME.DARK
                  ? "#ffffff"
                  : "#374151"
              };
              font-family: ${
                isHighlighted ? "'Conthrax Semi-Bold', 'Arial', sans-serif" : "inherit"
              };
              font-size: ${isHighlighted ? "14px" : "12px"};
              font-weight: ${isHighlighted ? "bold" : "normal"};
            ">${param.seriesName}</span>
          </div>
          <span style="
            color: ${
              isHighlighted
                ? tooltipColors.secondaryColor
                : globalTheme === THEME.DARK
                ? "#ffffff"
                : "#374151"
            };
            margin-left: 12px;
            font-family: ${isHighlighted ? "'Conthrax Semi-Bold', 'Arial', sans-serif" : "inherit"};
            font-size: ${isHighlighted ? "14px" : "12px"};
            font-weight: ${isHighlighted ? "bold" : "normal"};
            display: inline-block;
            text-align: left;
            min-width: 80px;
            flex-shrink: 0;
          ">$${getUSANumberFormat(param.value, false, true)}</span>
        </div>`;
      }
    });

    content += "</div>";
    return `<div style="
      background: ${globalTheme === THEME.DARK ? "#2A2A3D" : "#ffffff"};
      border: 1px solid ${globalTheme === THEME.DARK ? "#3A3A50" : "#E5E7EB"};
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      overflow: hidden;
    ">${header}${content}</div>`;
  };

  const chartDataSets = calculateChartData();

  // Determine the initial window of visible records (by index)
  // when there is no prior zoom state.
  // This ensures we always show at most INITIAL_VISIBLE_RECORDS items
  // on the first render, regardless of filter.
  const useInitialWindow = !zoomStateRef.current;
  const initialEndIndex = useInitialWindow
    ? Math.min(INITIAL_VISIBLE_RECORDS - 1, Math.max((chartData?.length || 0) - 1, 0))
    : undefined;

  if (loading) {
    return (
      <div
        className={`flex justify-center items-center ${className}`}
        style={{
          height,
          backgroundColor: globalTheme === THEME.DARK ? "#000000" : "#ffffff",
          borderRadius: "8px",
          padding: "10px",
        }}
      >
        <div className="text-center">
          <i className="pi pi-spin pi-spinner text-2xl text-blue-400 mb-2" />
          <p
            className={`text-sm ${globalTheme === THEME.DARK ? "text-gray-300" : "text-gray-600"}`}
          >
            Loading chart...
          </p>
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div
        className={`flex justify-center items-center ${className}`}
        style={{
          height,
          backgroundColor: globalTheme === THEME.DARK ? "#000000" : "#ffffff",
          borderRadius: "8px",
          padding: "5px",
        }}
      >
        <div className="text-center">
          <i className="pi pi-chart-line text-3xl text-gray-400 mb-2" />
          <p
            className={`text-lg ${globalTheme === THEME.DARK ? "text-gray-300" : "text-gray-500"}`}
          >
            No data available
          </p>
        </div>
      </div>
    );
  }

  const fmtIq = (v: number | null) => (v != null && Number.isFinite(v) ? v.toFixed(1) : "—");
  const fmtMoney = (v: number | null) =>
    v != null && Number.isFinite(v)
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(v)
      : "—";
  const iqColor = (v: number | null) => {
    if (v == null || !Number.isFinite(v)) return globalTheme === THEME.DARK ? "#94a3b8" : "#64748b";
    return v >= 100 ? "#22c55e" : "#ef4444";
  };

  return (
    <div
      className={`w-full ${className}`}
      style={{
        position: "relative",
        backgroundColor: globalTheme === THEME.DARK ? "#000000" : "#ffffff",
        borderRadius: "8px",
      }}
    >
      {alignTooltip && (
        <div
          style={{
            position: "absolute",
            left: alignTooltip.x + 10,
            top: alignTooltip.y + 10,
            transform:
              alignTooltip.dataIndex === 0
                ? "translate(0, calc(-100% - 8px))"
                : "translate(-50%, calc(-100% - 8px))",
            zIndex: 100,
            backgroundColor: globalTheme === THEME.DARK ? "#2A2A3D" : "#ffffff",
            border: `1px solid ${globalTheme === THEME.DARK ? "#3A3A50" : "#E5E7EB"}`,
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            overflow: "hidden",
            pointerEvents: "none",
            minWidth: 220,
          }}
        >
          <div
            style={{
              backgroundColor: globalTheme === THEME.DARK ? "#3A3A50" : "#E5E7EB",
              color: globalTheme === THEME.DARK ? "#ffffff" : "#374151",
              padding: "7px 12px",
              fontWeight: "bold",
              fontSize: 12,
              textAlign: "center",
              borderBottom: `1px solid ${globalTheme === THEME.DARK ? "#4A4A60" : "#e0e0e0"}`,
              fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
            }}
          >
            Plan Yr {alignTooltip.planYear}&nbsp;·&nbsp;{alignTooltip.year}&nbsp;·&nbsp;Age{" "}
            {alignTooltip.insuredAge}
          </div>
          <div style={{ padding: "8px 12px" }}>
            {(
              [
                {
                  label: "TrueIQ (Account Value)",
                  value: fmtIq(alignTooltip.acIQ),
                  color: iqColor(alignTooltip.acIQ),
                },
                {
                  label: "TrueIQ (Align)",
                  value: fmtIq(alignTooltip.alignIQ),
                  color: iqColor(alignTooltip.alignIQ),
                },
                {
                  label: "Interest Credits",
                  value: fmtMoney(alignTooltip.interestCredits),
                  color: globalTheme === THEME.DARK ? "#ffffff" : "#374151",
                },
              ] as { label: string; value: string; color: string }[]
            )
              // eslint-disable-next-line
              .map(({ label, value, color }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "4px 0",
                    borderBottom: `1px solid ${globalTheme === THEME.DARK ? "#4A4A60" : "#E5E7EB"}`,
                    gap: 16,
                  }}
                >
                  <span
                    style={{
                      color: globalTheme === THEME.DARK ? "#94a3b8" : "#64748b",
                      fontSize: 11,
                      fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      color,
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
      {/* Chart container */}
      <div
        style={{
          height,
          padding: "10px",
        }}
      >
        <ReactECharts
          onEvents={{
            click: (params: any) => {
              if (params.seriesName) {
                selectedSeriesRef.current = params.seriesName;
              }
            },
            mouseover: (params: any) => {
              if (params.seriesName) {
                selectedSeriesRef.current = params.seriesName;
              }
              if (params.componentType === "series" && params.seriesName === "Align TrueIQ") {
                echartsRef.current?.getEchartsInstance?.()?.dispatchAction({ type: "hideTip" });
                const d = params.data as any;
                setAlignTooltip({
                  x: params.event?.offsetX ?? 0,
                  y: params.event?.offsetY ?? 0,
                  dataIndex: params.dataIndex ?? 0,
                  acIQ: d?.acIQ ?? null,
                  alignIQ: d?.alignIQ ?? null,
                  interestCredits: d?.interestCredits ?? null,
                  planYear: d?.planYear ?? 0,
                  year: d?.year ?? 0,
                  insuredAge: d?.insuredAge ?? 0,
                });
              }
            },
            mouseout: () => {
              selectedSeriesRef.current = null;
              setAlignTooltip(null);
            },
            dataZoom: (params: any) => {
              zoomStateRef.current = { start: params.start, end: params.end };
            },
          }}
          option={{
            title: {
              text: "",
              bottom: 40,
              textStyle: {
                color,
                fontSize: 14,
                fontFamily: "Conthrax Semi-Bold",
              },
            },
            tooltip: {
              trigger: "axis",
              axisPointer: {
                type: "none",
              },
              backgroundColor: globalTheme === THEME.DARK ? "#2A2A3D" : "#ffffff",
              borderColor: globalTheme === THEME.DARK ? "#3A3A50" : "#e0e0e0",
              borderWidth: 1,
              borderRadius: 8,
              padding: 0,
              textStyle: {
                color,
                fontSize: 12,
              },
              formatter(params: any) {
                if (!params || params.length === 0) return "";
                if (tooltipFormatterOverride) return tooltipFormatterOverride(params);
                return createTooltipHTML(params);
              },
              extraCssText: `
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              border: none !important;
            `,
            },
            legend: {
              show: false,
            },
            grid: {
              left: "1%",
              right: "3%",
              bottom: "14%",
              top: chartGridTop ?? "5%",
              containLabel: true,
              show: false,
            },
            dataZoom: [
              {
                type: "slider",
                show: true,
                // When there is no existing zoom state (first render), use index-based
                // startValue/endValue so we show exactly INITIAL_VISIBLE_RECORDS items.
                // Once the user zooms, we preserve their percentage-based zoom state.
                start: useInitialWindow ? undefined : zoomStateRef.current?.start || 0,
                end: useInitialWindow
                  ? undefined
                  : zoomStateRef.current?.end ||
                    Math.min(100, (INITIAL_VISIBLE_RECORDS / (chartData?.length || 1)) * 100),
                startValue: useInitialWindow ? 0 : undefined,
                endValue: useInitialWindow ? initialEndIndex : undefined,
                minValueSpan: 1,
                maxValueSpan: chartData?.length || 1,
                height: 20,
                bottom: 10,
                borderColor: "transparent",
                fillerColor:
                  globalTheme === THEME.DARK ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
                handleStyle: {
                  color: globalTheme === THEME.DARK ? "#ffffff" : "#333333",
                  borderColor: globalTheme === THEME.DARK ? "#4A4A60" : "#E5E7EB",
                },
                textStyle: {
                  color,
                  fontSize: 10,
                },
                showDetail: true,
                showDataShadow: true,
                realtime: true,
                filterMode: "filter",
                xAxisIndex: 0,
              },
            ],
            xAxis: {
              type: "category",
              data: getChartLabelsData(),
              axisLabel: {
                color,
                fontFamily: "Conthrax Semi-Bold",
                fontSize: 10,
              },
              boundaryGap: false,
              axisLine: {
                show: false,
                lineStyle: {
                  color,
                },
                onZero: true,
              },
              axisTick: {
                alignWithLabel: true,
              },
            },
            yAxis: {
              splitLine: {
                show: false,
              },
              max(value: any) {
                return Math.round(value.max * 1.2);
              },
              min: 0,
              axisLabel: {
                color,
                formatter: (value: number) => `$${intToString(value, true)}`,
                fontFamily: "Conthrax Semi-Bold",
                fontSize: 10,
              },
              boundaryGap: [0, "100%"],
              axisLine: {
                show: false,
              },
            },
            series: [
              ...chartDataSets.map((dataset: any) => ({
                ...dataset,
                name: dataset.name,
                type: dataset.type,
                data: dataset.data,
                smooth: dataset.smooth,
                showSymbol: dataset.showSymbol,
                lineStyle: dataset.lineStyle,
                itemStyle: dataset.itemStyle,
                areaStyle: dataset.areaStyle,
                ...(firstLineSeriesExtras ?? {}),
              })),
              ...(appendSeries ?? []),
            ],
          }}
          ref={echartsRef}
          style={{ width: "100%", height: "100%", margin: 0, padding: 0 }}
          notMerge
          lazyUpdate={false}
        />
      </div>
    </div>
  );
};

export default AssetValueLineChart;
